<template>
  <div class="results-notices" :class="noticeModifiers">
    <div v-if="broadcastMessages.length" class="results-notices__broadcasts">
      <sp-broadcast
        v-for="(message, index) in broadcastMessages"
        :key="index"
        class="results-notices__broadcast"
        :level="message.level"
        variant="card"
        :icon="message.icon"
        :collapsable="Boolean(message.collapsable)"
        :dismissable="Boolean(message.dismissable)"
        inline
      >
        <span v-if="message.title" slot="title"><FinderRichText :html="message.title" /></span>
        <div class="broadcast-message-text">
          <FinderRichText :html="message.text" />
        </div>
        <sp-button
          v-if="message.action"
          ref="actions"
          slot="action"
          tag="a"
          :href="message.action.link"
          size="small"
          color="primary"
          ><FinderRichText :html="message.action.content"
        /></sp-button>
      </sp-broadcast>
    </div>
    <slot></slot>
  </div>
</template>

<script setup>
import { computed, shallowRef, useTemplateRef, watchPostEffect } from "vue";
import FinderRichText from "#ce/components/SpFinder/components/FinderRichText.ce.vue";
import { whenMountReady } from "#ce/framework/mount-ready";

const props = defineProps({
  /**
   * The finder's results payload: its `broadcast_messages`.
   *
   * @type {Object}
   */
  payload: {
    type: Object,
    required: true,
  },
  /**
   * The website the visitor is on. A broadcast limited to other websites is not shown.
   *
   * @type {String}
   */
  currentWebsite: {
    type: String,
    default: undefined,
  },
  /**
   * Whether a notice part the page places in the block shows anything.
   *
   * @type {Boolean}
   */
  hasParts: {
    type: Boolean,
    default: false,
  },
});

function shownOnCurrentWebsite(message) {
  const allowed = message.allowed_websites;
  return !allowed?.length || allowed.includes(props.currentWebsite);
}

const broadcastMessages = computed(() => (props.payload.broadcast_messages ?? []).filter(shownOnCurrentWebsite));

// An action button has no width until its code loads and it mounts, and the banner's text rewraps
// when it does. The notices stay hidden until every action has mounted or failed to load, so they
// first paint at their final size.
const actions = useTemplateRef("actions");
const settledMessages = shallowRef();

watchPostEffect(async () => {
  const messages = broadcastMessages.value;

  await Promise.allSettled((actions.value ?? []).map(whenMountReady));

  if (messages === broadcastMessages.value) {
    settledMessages.value = messages;
  }
});

const noticeModifiers = computed(() => ({
  "--empty": broadcastMessages.value.length === 0 && !props.hasParts,
  "--pending":
    broadcastMessages.value.some((message) => message.action) && settledMessages.value !== broadcastMessages.value,
}));
</script>

<style lang="scss" scoped>
@use "@assets/mixins/breakpoint" as *;
@use "@ce/components/SpFinder/styles/results-container" as *;

.results-notices {
  --margin: var(--sp-comp-finder-results-notices-margin-mobile, var(--sp-ref-scale-16) auto);
  --margin-desktop: var(--sp-comp-finder-results-notices-margin, var(--sp-ref-scale-40) auto);
  --broadcast-margin: var(--sp-comp-finder-results-notices-broadcast-margin-mobile, 0);
  --broadcast-margin-desktop: var(--sp-comp-finder-results-notices-broadcast-margin, 0);

  @include finder-results-container(
    var(--sp-comp-finder-results-notices-padding-inline, var(--sp-comp-finder-results-padding-inline, 10px))
  );

  display: flex;
  flex-direction: column;
  gap: var(--sp-ref-scale-16);
  margin: var(--margin);

  @include sp-breakpoint(sm) {
    margin: var(--margin-desktop);
  }

  &.--empty,
  &.--pending {
    display: none;
  }

  &__broadcasts {
    margin: var(--broadcast-margin);

    @include sp-breakpoint(sm) {
      margin: var(--broadcast-margin-desktop);
    }
  }

  &__broadcast {
    --sp-comp-finder-result-rich-text-link-text-decoration: var(--sp-comp-broadcast-message-link-decoration, none);
    --sp-comp-finder-result-rich-text-link-text-decoration-hover: var(
      --sp-comp-broadcast-message-link-hover-decoration,
      underline
    );
    --sp-comp-finder-result-rich-text-link-font-family: var(
      --sp-comp-broadcast-message-text-typeface,
      var(--sp-sys-body-font-family)
    );

    @each $level in ("info", "warning", "alert") {
      &[level="#{$level}"] {
        --sp-comp-finder-result-rich-text-link-color: var(--sp-comp-broadcast-message-level-#{$level}-link-color);
        --sp-comp-finder-result-rich-text-link-color-hover: var(
          --sp-comp-broadcast-message-level-#{$level}-link-hover-color
        );
        --sp-comp-finder-result-rich-text-link-font-weight: var(
          --sp-comp-broadcast-message-level-#{$level}-link-font-weight,
          normal
        );
      }
    }

    // The title and text arrive as editor HTML, whose paragraphs run on as one line in an inline banner.
    :deep(p) {
      display: inline;
      margin: 0;

      &::after {
        content: " ";
      }
    }
  }

  &__broadcast + &__broadcast {
    margin-top: var(--sp-comp-broadcast-message-spacing, 1rem);
  }
}
</style>
